import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Button, Typography, Alert, Space, Tooltip, message } from 'antd';
import {
  WalletOutlined,
  CheckCircleFilled,
  CopyOutlined,
  DisconnectOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { updatePayoutDestination } from '../firebase/firestore';
import './WalletConnectModal.css';

const { Text, Title } = Typography;

// Adresse user-friendly (EQ.../UQ..., 48 caractères base64url) ou raw (0:<64 hex>)
const FRIENDLY_ADDRESS_RE = /^[EU][Qf][A-Za-z0-9_-]{46}$/;
const RAW_ADDRESS_RE = /^-?\d+:[a-fA-F0-9]{64}$/;

export const isValidTonAddress = (value: string): boolean => {
  const v = value.trim();
  return FRIENDLY_ADDRESS_RE.test(v) || RAW_ADDRESS_RE.test(v);
};

const shortenAddress = (address: string) =>
  address.length > 16 ? `${address.slice(0, 8)}…${address.slice(-6)}` : address;

interface WalletConnectModalProps {
  open: boolean;
  onClose: () => void;
}

const WalletConnectModal: React.FC<WalletConnectModalProps> = ({ open, onClose }) => {
  const { user, firebaseUser, refreshUser } = useAuth();
  const { t } = useI18n();
  const w = t.app.layout;

  const currentAddress = user?.agent?.tonAddress || '';
  const [address, setAddress] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setAddress(currentAddress);
      setEditing(!currentAddress);
      setError(null);
    }
  }, [open, currentAddress]);

  const trimmed = address.trim();
  const isValid = useMemo(() => isValidTonAddress(trimmed), [trimmed]);
  const showInvalid = trimmed.length > 0 && !isValid;
  const unchanged = trimmed === currentAddress;

  const persist = async (value: string, successText: string) => {
    if (!firebaseUser?.uid) {
      setError(t.app.common.mustBeLoggedIn);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updatePayoutDestination(firebaseUser.uid, value);
      await refreshUser();
      message.success(successText);
      onClose();
    } catch (err: any) {
      setError(err?.message || t.settings.updateError);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!isValid || unchanged) return;
    persist(trimmed, w.walletSaved);
  };

  const handleDisconnect = () => {
    Modal.confirm({
      title: w.walletDisconnectConfirmTitle,
      content: w.walletDisconnectConfirmBody,
      okText: w.walletDisconnect,
      okButtonProps: { danger: true },
      cancelText: t.app.common.cancel,
      onOk: () => persist('', w.walletDisconnected),
    });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentAddress);
      message.success(t.app.common.copied);
    } catch {
      /* clipboard indisponible */
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      width={440}
      className="wallet-modal"
    >
      <div className="wallet-modal-header">
        <div className={`wallet-modal-icon ${currentAddress ? 'connected' : ''}`}>
          {currentAddress ? <CheckCircleFilled /> : <WalletOutlined />}
        </div>
        <Title level={4} className="wallet-modal-title">
          {currentAddress ? w.walletConnectedTitle : w.walletTitle}
        </Title>
        <Text type="secondary" className="wallet-modal-subtitle">
          {currentAddress ? w.walletConnectedSubtitle : w.walletSubtitle}
        </Text>
      </div>

      {currentAddress && !editing && (
        <div className="wallet-modal-body">
          <div className="wallet-address-card">
            <Text className="wallet-address-label">{w.walletCurrentAddress}</Text>
            <div className="wallet-address-row">
              <Tooltip title={currentAddress}>
                <Text className="wallet-address-value" code>
                  {shortenAddress(currentAddress)}
                </Text>
              </Tooltip>
              <Tooltip title={t.app.common.copy}>
                <Button type="text" size="small" icon={<CopyOutlined />} onClick={handleCopy} />
              </Tooltip>
            </div>
          </div>

          <Space direction="vertical" size={8} className="wallet-modal-actions">
            <Button block icon={<EditOutlined />} onClick={() => setEditing(true)}>
              {w.walletChangeAddress}
            </Button>
            <Button block danger type="text" icon={<DisconnectOutlined />} onClick={handleDisconnect} loading={saving}>
              {w.walletDisconnect}
            </Button>
          </Space>
        </div>
      )}

      {(editing || !currentAddress) && (
        <div className="wallet-modal-body">
          <label htmlFor="wallet-ton-address" className="wallet-address-label">
            {w.walletAddressLabel}
          </label>
          <Input
            id="wallet-ton-address"
            size="large"
            autoFocus
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onPressEnter={handleSave}
            placeholder={t.settings.tonAddressPlaceholder}
            status={showInvalid ? 'error' : undefined}
            prefix={<WalletOutlined style={{ color: 'rgba(26,26,26,0.4)' }} />}
            suffix={isValid ? <CheckCircleFilled style={{ color: '#52c41a' }} /> : null}
            allowClear
          />
          <Text type={showInvalid ? 'danger' : 'secondary'} className="wallet-address-hint">
            {showInvalid ? w.walletInvalidAddress : w.walletHint}
          </Text>

          {error && <Alert type="error" showIcon message={error} className="wallet-modal-error" />}

          <Space direction="vertical" size={8} className="wallet-modal-actions">
            <Button
              type="primary"
              size="large"
              block
              loading={saving}
              disabled={!isValid || unchanged}
              onClick={handleSave}
              className="wallet-connect-btn"
            >
              {currentAddress ? w.walletUpdate : w.walletConnect}
            </Button>
            {currentAddress && (
              <Button block type="text" onClick={() => { setEditing(false); setAddress(currentAddress); setError(null); }}>
                {t.app.common.cancel}
              </Button>
            )}
          </Space>
        </div>
      )}
    </Modal>
  );
};

export default WalletConnectModal;
